import * as React from 'react';
import { connect } from 'react-redux';
import { PersonaSize, Panel, PanelType, ActionButton, IconButton, Link, Icon } from 'office-ui-fabric-react';
import { inIframe } from "../utils/common";
import ImagePersona from '../common/ImagePersona';
import { ApplicationState } from "../../store/index";
import { UserState } from '../../store/User';
import { CommonOperations } from '../../store/permissions';
import { PPMFeatures, Subscription, TenantState } from '../../store/Tenant';
import TenantCard from './TenantCard';
import EmailSettings from './EmailSettings';
import { AuthProvider } from '../../entities/common';
import { NavigationProps } from './Breadcrumbs';
import { PresentationModePersonalSettings } from './PresentationModePersonalSettings';
import DelegationModeSettings from './DelegationModeSettings/DelegationModeSettings';
import UserReleaseNotifications from './UserReleaseNotifications';
import { G2ReviewUrl } from '../utils/externalLinks';
import { DelegationScope, isDelegationEnabled } from '../../store/DelegationStore';

export const VIEWER_PREFIX = '[VIEWER]';

type OwnProps = NavigationProps & {
    allowChangeAccount: boolean;
    isOpen: boolean;
    onDismiss: () => void;
};

type StateProps = {
    subscription: Subscription;
    user: UserState;
    tenant: TenantState;
};

type Props = OwnProps & StateProps;

const AccountPanel = (props: Props) => {
    const { allowChangeAccount, user, tenant, isOpen, router, onDismiss, subscription } = props;
    const isTenantAvailable = !!tenant.id;

    const userName = getUserName(user);

    const isViewer = user.permissions?.common === CommonOperations.AdministrateView;
    const allowEditProfile = !isViewer && user.signInAuthProvider === AuthProvider.email;
    const presentationModeFeatureEnabled = Subscription.containsAny(subscription, [PPMFeatures.PresentationMode, PPMFeatures.PresentationModeUltra]);
    const canDelegateTimesheetApproval = isTenantAvailable && isDelegationEnabled(tenant, DelegationScope.TimesheetApproval) && !!user.canApprove?.timesheet;

    return (
        <Panel
            isOpen={isOpen}
            isLightDismiss
            type={PanelType.custom}
            customWidth="400px"
            onRenderHeader={(headerProps) => <PanelHeader {...headerProps} />}
            onDismiss={onDismiss}
            className='user-info-panel'>
            <ImagePersona size={PersonaSize.size48} info={user} text={user.name} secondaryText={user.email} onRenderPrimaryText={() => <>
                {userName}
                {allowEditProfile && <IconButton className="edit-profile" title="Edit Profile" menuIconProps={{ iconName: 'Edit' }}
                    href={`account/editprofile?redirectUri=${encodeURIComponent(router.location.pathname)}`}>
                </IconButton>}
            </>} />
            <div className="actions">
                <ActionButton className="sign-out"
                    iconProps={{ iconName: allowChangeAccount ? 'UserSync' : 'SignOut' }}
                    href={`account/signout${allowChangeAccount || inIframe() ? "?redirectUri=" + encodeURIComponent(router.location.pathname) : ""}`}>
                    {allowChangeAccount ? 'Change account' : 'Log out'}
                </ActionButton>
            </div>
            {
                user.availableTenants && <div className="tenants card-list">
                    {user.availableTenants.map((viewModel, index) => <TenantCard key={viewModel.id} tenant={viewModel} />)}
                </div>
            }
            {isTenantAvailable && <div className='sections'>
                <EmailSettings />
                {presentationModeFeatureEnabled && <PresentationModePersonalSettings />}
                {canDelegateTimesheetApproval && <DelegationModeSettings />}
                {!Subscription.contains(subscription, PPMFeatures.PartnersSupport) && <UserReleaseNotifications email={user.email} company={tenant.companyName} />}
            </div>}
             <Link
                    className="text-link g2-review-link"
                    href={G2ReviewUrl}
                    target="_blank">
                    <Icon iconName="G2Logo" /> Please review PPM Express on G2
                </Link>
        </Panel>
    );
}

const getUserName = (user: UserState) => {
    const isViewer = user.permissions?.common === CommonOperations.AdministrateView;
    return isViewer
        ? `${VIEWER_PREFIX} ${user.name}`
        : user.name;
}

const PanelHeader = () => (
    <div className="ms-Panel-header">
        <p className="ms-Panel-headerText">My account</p>
        <div className='ms-Panel-secondaryText'>View current logon account and switch Organization context.</div>
    </div>
);

function mapStateToProps(state: ApplicationState, ownProps?: OwnProps): StateProps {
    return {
        subscription: state.tenant.subscription,
        user: state.user,
        tenant: state.tenant,
    };
}

export default connect(mapStateToProps)(AccountPanel);
